import { redirect } from 'react-router-dom';

import { call } from '@spacefill/shared/src/utils/Backend';

const AppointmentsProviderView = async () =>
    import('../../pages/appointments/AppointmentsProviderView').then((module) => ({
        Component: module.AppointmentsProviderView,
    }));

const AppointmentsShipperView = async () =>
    import('../../pages/appointments/AppointmentsShipperView').then((module) => ({
        Component: module.AppointmentsShipperView,
    }));

const AppointmentModalRoute = async () =>
    import('./AppointmentModalRoute').then((module) => ({
        Component: module.AppointmentModalRoute,
    }));

const RecurringAppointmentModalRoute = async () =>
    import('./RecurringAppointmentModalRoute').then((module) => ({
        Component: module.RecurringAppointmentModalRoute,
    }));
const AppointmentDetailsModalRoute = async () =>
    import('../../pages/appointments/AppointmentDetailsModalRoute').then((module) => ({
        Component: module.AppointmentDetailsModalRoute,
    }));

const editAppointmentFromAppointmentIdLoader = async ({ params: { appointmentId } }) => {
    const appointment = await call(`appointments/${appointmentId}`);
    return { appointment, edit: true };
};

const appointmentDetailsLoader = async ({ params: { appointmentId, warehouseId, viewId } }) => {
    const appointment = await call(`appointments/${appointmentId}`);
    if (!appointment || appointment.status === 'cancelled' || appointment.status === 'to_schedule') {
        let redirectUrl = '/appointments/list/';
        if (warehouseId) {
            redirectUrl = `/appointments/warehouses/${warehouseId}/list`;
            if (viewId) {
                redirectUrl += `/view/${viewId}`;
            }
        } else if (viewId) {
            redirectUrl = `/appointments/list/view/${viewId}`;
        }

        throw redirect(redirectUrl);
    }

    const body = {};
    if (warehouseId) {
        body.warehouseIds = [warehouseId];
    } else if (appointment.warehouseId) {
        body.warehouseIds = [appointment.warehouseId];
    }
    const docksData = await call('/appointments/docks', body, 'POST');
    const docks = docksData?.docks || [];

    return { appointment, docks };
};

const getAppointmentsRoutes = (user) => {
    if (user?.carrierId) return [];

    const shipperCanShowAppointments =
        user.role === 'SHIPPER' && user.logisticProviders.some((lsp) => lsp.settings?.appointmentScheduling?.enabled);

    const providerCanShowAppointments =
        user.role === 'PROVIDER' && user.logisticProvider?.settings?.appointmentScheduling?.enabled;

    const lspAppointmentsRoutes = [
        {
            path: '/appointments/',
            lazy: AppointmentsProviderView,
            children: [
                {
                    path: 'edit/appointment/:appointmentId',
                    lazy: AppointmentModalRoute,
                    loader: editAppointmentFromAppointmentIdLoader,
                },
                {
                    path: 'edit/recurring/:appointmentId',
                    lazy: RecurringAppointmentModalRoute,
                    loader: editAppointmentFromAppointmentIdLoader,
                },
            ],
        },
        {
            path: '/appointments/calendar/',
            lazy: AppointmentsProviderView,
        },
        {
            path: '/appointments/warehouses/:warehouseId/calendar',
            lazy: AppointmentsProviderView,
        },
        {
            path: '/appointments/list/',
            lazy: AppointmentsProviderView,
            children: [
                {
                    path: 'edit/appointment/:appointmentId',
                    lazy: AppointmentModalRoute,
                    loader: editAppointmentFromAppointmentIdLoader,
                },
                {
                    path: 'edit/recurring/:appointmentId',
                    lazy: RecurringAppointmentModalRoute,
                    loader: editAppointmentFromAppointmentIdLoader,
                },
                {
                    path: ':appointmentId',
                    lazy: AppointmentDetailsModalRoute,
                    loader: appointmentDetailsLoader,
                },
            ],
        },
        {
            path: '/appointments/list/view/:viewId',
            lazy: AppointmentsProviderView,
            children: [
                {
                    path: ':appointmentId',
                    lazy: AppointmentDetailsModalRoute,
                    loader: appointmentDetailsLoader,
                },
            ],
        },
        {
            path: '/appointments/warehouses/:warehouseId/list',
            lazy: AppointmentsProviderView,
            children: [
                {
                    path: ':appointmentId',
                    lazy: AppointmentDetailsModalRoute,
                    loader: appointmentDetailsLoader,
                },
            ],
        },
        {
            path: '/appointments/warehouses/:warehouseId/list/view/:viewId',
            lazy: AppointmentsProviderView,
            children: [
                {
                    path: ':appointmentId',
                    lazy: AppointmentDetailsModalRoute,
                    loader: appointmentDetailsLoader,
                },
            ],
        },
    ];
    const shipperAppointmentsRoutes = [
        {
            path: '/appointments/list',
            lazy: AppointmentsShipperView,
            children: [
                {
                    path: ':appointmentId',
                    lazy: AppointmentDetailsModalRoute,
                    loader: appointmentDetailsLoader,
                },
            ],
        },
        {
            path: '/appointments/list/view/:viewId',
            lazy: AppointmentsProviderView,
        },
        {
            path: '/appointments/warehouses/:warehouseId/list',
            lazy: AppointmentsShipperView,
        },
        {
            path: '/appointments/list/view/:viewId',
            lazy: AppointmentsProviderView,
            children: [
                {
                    path: ':appointmentId',
                    lazy: AppointmentDetailsModalRoute,
                    loader: appointmentDetailsLoader,
                },
            ],
        },
        {
            path: '/appointments/warehouses/:warehouseId/list',
            lazy: AppointmentsProviderView,
            children: [
                {
                    path: ':appointmentId',
                    lazy: AppointmentDetailsModalRoute,
                    loader: appointmentDetailsLoader,
                },
            ],
        },
        {
            path: '/appointments/warehouses/:warehouseId/list/view/:viewId',
            lazy: AppointmentsProviderView,
            children: [
                {
                    path: ':appointmentId',
                    lazy: AppointmentDetailsModalRoute,
                    loader: appointmentDetailsLoader,
                },
            ],
        },
    ];
    if (providerCanShowAppointments) {
        return lspAppointmentsRoutes;
    } else if (shipperCanShowAppointments) {
        return shipperAppointmentsRoutes;
    }

    return [];
};

export { getAppointmentsRoutes };
